<script>
  import { createEventDispatcher } from 'svelte';
  import { API_BASE_URL } from '../../config.js';
  
  const dispatch = createEventDispatcher();
  
  // Props
  export let fileType = 'document';
  export let multiple = true;
  export let maxFiles = 5;
  export let accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif';
  export let maxSize = 10 * 1024 * 1024; // 10MB
  export let label = '파일 업로드';
  export let description = '';
  export let disabled = false;
  export let existingFiles = [];
  
  // 상태 관리
  let fileInput;
  let selectedFiles = [];
  let uploading = false;
  let uploadProgress = 0;
  let dragOver = false;
  let errors = [];
  let debugLogs = [];
  
  // 파일 선택 핸들러
  function handleFileSelect(event) {
    const files = Array.from(event.target.files);
    processFiles(files);
  }
  
  // 드래그 앤 드롭 핸들러
  function handleDragOver(event) {
    event.preventDefault();
    dragOver = true;
  }
  
  function handleDragLeave(event) {
    event.preventDefault();
    dragOver = false;
  }
  
  function handleDrop(event) {
    event.preventDefault();
    dragOver = false;
    
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  }
  
  // 파일 처리
  function processFiles(files) {
    errors = [];
    
    // 파일 개수 체크
    if (files.length > maxFiles) {
      errors.push(`최대 ${maxFiles}개의 파일만 업로드할 수 있습니다.`);
      return;
    }
    
    // 파일 크기 및 타입 체크
    const validFiles = [];
    for (const file of files) {
      if (file.size > maxSize) {
        errors.push(`${file.name}: 파일 크기가 ${Math.round(maxSize / 1024 / 1024)}MB를 초과합니다.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      selectedFiles = validFiles;
      dispatch('files-selected', { files: validFiles });
    }
  }
  
  // 파일 업로드
  async function uploadFiles() {
    if (selectedFiles.length === 0) return;
    
    uploading = true;
    uploadProgress = 0;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // 파일 추가
      for (const file of selectedFiles) {
        formData.append('files', file);
      }
      
      // 메타데이터 추가
      formData.append('fileType', fileType);
      formData.append('description', description);
      
      const endpoint = selectedFiles.length === 1 ? '/upload' : '/upload-multiple';
      const uploadUrl = `${API_BASE_URL}/api/files${endpoint}`;
      
      console.log('업로드 시작:', {
        url: uploadUrl,
        fileCount: selectedFiles.length,
        fileNames: selectedFiles.map(f => f.name),
        token: token ? '토큰 있음' : '토큰 없음'
      });
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log('응답 상태:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('서버 에러 응답:', errorText);
        errors.push(`서버 오류 (${response.status}): ${response.statusText} - ${errorText}`);
        return;
      }
      
      const result = await response.json();
      console.log('업로드 결과:', result);
      
      if (result.success) {
        dispatch('upload-success', { 
          files: result.files || [result.file],
          message: result.message 
        });
        
        // 초기화
        selectedFiles = [];
        fileInput.value = '';
        
      } else {
        const serverError = `서버 응답 실패: ${result.message || '알 수 없는 오류'} [응답 데이터: ${JSON.stringify(result)}]`;
        errors.push(serverError);
      }
      
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      
      // 모바일 디버깅을 위한 상세 에러 정보
      let errorMessage = '파일 업로드 중 오류가 발생했습니다.';
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage += ' [네트워크 오류: 서버에 연결할 수 없습니다]';
      } else if (error.name === 'TypeError') {
        errorMessage += ` [타입 오류: ${error.message}]`;
      } else if (error.message) {
        errorMessage += ` [오류 내용: ${error.message}]`;
      }
      
      // 추가 디버그 정보
      errorMessage += ` [오류 타입: ${error.name || '알 수 없음'}]`;
      
      if (error.stack) {
        const stackLines = error.stack.split('\n').slice(0, 3);
        errorMessage += ` [스택: ${stackLines.join(' → ')}]`;
      }
      
      errors.push(errorMessage);
    } finally {
      uploading = false;
      uploadProgress = 0;
    }
  }
  
  // 파일 제거
  function removeFile(index) {
    selectedFiles = selectedFiles.filter((_, i) => i !== index);
    dispatch('files-selected', { files: selectedFiles });
  }
  
  // 파일 크기 포맷팅
  function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  // 파일 타입 아이콘
  function getFileIcon(fileName) {
    const ext = fileName.split('.').pop().toLowerCase();
    
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'txt': return '📃';
      default: return '📎';
    }
  }
</script>

<div class="file-upload-container">
  <div class="file-upload-header">
    <h4>{label}</h4>
    {#if description}
      <p class="description">{description}</p>
    {/if}
  </div>
  
  <!-- 파일 선택 영역 -->
  <div 
    class="file-drop-zone" 
    class:drag-over={dragOver}
    class:disabled={disabled}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
  >
    <input 
      bind:this={fileInput}
      type="file" 
      {accept}
      {multiple}
      {disabled}
      on:change={handleFileSelect}
      class="file-input"
    />
    
    <div class="drop-zone-content">
      <div class="upload-icon">📁</div>
      <p class="drop-text">
        파일을 여기로 드래그하거나 <button type="button" class="browse-button" on:click={() => fileInput.click()}>파일 선택</button>
      </p>
      <p class="file-info">
        {#if multiple}
          최대 {maxFiles}개 파일, 
        {/if}
        파일당 최대 {Math.round(maxSize / 1024 / 1024)}MB
      </p>
    </div>
  </div>
  
  <!-- 에러 메시지 -->
  {#if errors.length > 0}
    <div class="error-messages">
      {#each errors as error}
        <p class="error-message">❌ {error}</p>
      {/each}
    </div>
  {/if}

  <!-- 디버그 로그 -->
  {#if debugLogs.length > 0}
    <div class="debug-logs">
      <h5>디버그 로그:</h5>
      {#each debugLogs as log}
        <div class="debug-log-item">
          <div class="debug-timestamp">🕒 {log.timestamp}</div>
          <div class="debug-message">
            <strong>오류 메시지:</strong> {log.message}
          </div>
          <div class="debug-details">
            <strong>API URL:</strong> {log.apiUrl}
          </div>
          <div class="debug-details">
            <strong>파일 타입:</strong> {log.fileType}
          </div>
          <div class="debug-details">
            <strong>선택된 파일:</strong>
            {#each log.selectedFiles as file}
              <div class="debug-file">- {file.name} ({file.size} bytes, {file.type})</div>
            {/each}
          </div>
          {#if log.stack}
            <details class="debug-stack">
              <summary>스택 트레이스 보기</summary>
              <pre class="stack-trace">{log.stack}</pre>
            </details>
          {/if}
        </div>
      {/each}
      <button type="button" class="clear-logs-button" on:click={() => debugLogs = []}>
        🗑️ 로그 지우기
      </button>
    </div>
  {/if}
  
  <!-- 선택된 파일 목록 -->
  {#if selectedFiles.length > 0}
    <div class="selected-files">
      <h5>선택된 파일:</h5>
      {#each selectedFiles as file, index}
        <div class="file-item">
          <span class="file-icon">{getFileIcon(file.name)}</span>
          <div class="file-details">
            <span class="file-name">{file.name}</span>
            <span class="file-size">{formatFileSize(file.size)}</span>
          </div>
          <button 
            type="button" 
            class="remove-button"
            on:click={() => removeFile(index)}
            disabled={uploading}
          >
            ✕
          </button>
        </div>
      {/each}
    </div>
  {/if}
  
  <!-- 업로드 버튼 -->
  {#if selectedFiles.length > 0}
    <div class="upload-actions">
      <button 
        type="button"
        class="upload-button"
        disabled={uploading || disabled}
        on:click={uploadFiles}
      >
        {#if uploading}
          <span class="spinner"></span>
          업로드 중...
        {:else}
          📤 파일 업로드
        {/if}
      </button>
    </div>
  {/if}
  
  <!-- 기존 파일 목록 -->
  {#if existingFiles.length > 0}
    <div class="existing-files">
      <h5>업로드된 파일:</h5>
      {#each existingFiles as file}
        <div class="file-item existing">
          <span class="file-icon">{getFileIcon(file.original_name)}</span>
          <div class="file-details">
            <span class="file-name">{file.original_name}</span>
            <span class="file-size">{formatFileSize(file.file_size)}</span>
            <span class="file-date">{new Date(file.created_at).toLocaleDateString()}</span>
          </div>
          <button 
            type="button" 
            class="download-button"
            on:click={() => dispatch('download-file', { file })}
          >
            ⬇️
          </button>
          <button 
            type="button" 
            class="remove-button"
            on:click={() => dispatch('delete-file', { file })}
          >
            🗑️
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .file-upload-container {
    width: 100%;
    max-width: 100%;
    margin: 1rem 0;
  }
  
  .file-upload-header h4 {
    margin: 0 0 0.5rem 0;
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  .description {
    margin: 0 0 1rem 0;
    color: #666;
    font-size: 0.9rem;
  }
  
  .file-drop-zone {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    transition: all 0.3s ease;
    background: #f9fafb;
    cursor: pointer;
    position: relative;
  }
  
  .file-drop-zone:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  .file-drop-zone.drag-over {
    border-color: #3b82f6;
    background: #dbeafe;
    transform: scale(1.02);
  }
  
  .file-drop-zone.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .file-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
  
  .file-input:disabled {
    cursor: not-allowed;
  }
  
  .drop-zone-content {
    pointer-events: none;
  }
  
  .upload-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  .drop-text {
    margin: 0 0 0.5rem 0;
    color: #374151;
    font-size: 1rem;
  }
  
  .browse-button {
    color: #3b82f6;
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
    pointer-events: all;
    font-size: inherit;
  }
  
  .browse-button:hover {
    color: #2563eb;
  }
  
  .file-info {
    margin: 0;
    color: #6b7280;
    font-size: 0.875rem;
  }
  
  .error-messages {
    margin: 1rem 0;
  }
  
  .error-message {
    color: #dc2626;
    font-size: 0.875rem;
    margin: 0.5rem 0;
  }
  
  .selected-files,
  .existing-files {
    margin: 1rem 0;
  }
  
  .selected-files h5,
  .existing-files h5 {
    margin: 0 0 0.5rem 0;
    color: #374151;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .file-item {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin: 0.5rem 0;
    background: white;
  }
  
  .file-item.existing {
    background: #f3f4f6;
  }
  
  .file-icon {
    font-size: 1.5rem;
    margin-right: 0.75rem;
  }
  
  .file-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .file-name {
    font-weight: 500;
    color: #374151;
    font-size: 0.9rem;
  }
  
  .file-size,
  .file-date {
    color: #6b7280;
    font-size: 0.8rem;
  }
  
  .remove-button,
  .download-button {
    background: none;
    border: none;
    font-size: 1rem;
    cursor: pointer;
    padding: 0.25rem;
    margin-left: 0.5rem;
  }
  
  .remove-button:hover {
    color: #dc2626;
  }
  
  .download-button:hover {
    color: #3b82f6;
  }
  
  .upload-actions {
    margin: 1rem 0;
    text-align: center;
  }
  
  .upload-button {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-size: 1rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: background-color 0.2s;
  }
  
  .upload-button:hover:not(:disabled) {
    background: #2563eb;
  }
  
  .upload-button:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
  
  .spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid #ffffff;
    border-radius: 50%;
    border-top-color: transparent;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  .debug-logs {
    margin: 1rem 0;
    padding: 1rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
  }
  
  .debug-logs h5 {
    margin: 0 0 1rem 0;
    color: #495057;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .debug-log-item {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1rem;
    margin: 0.5rem 0;
  }
  
  .debug-timestamp {
    color: #6c757d;
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
  
  .debug-message {
    color: #dc3545;
    margin: 0.5rem 0;
    font-size: 0.9rem;
  }
  
  .debug-details {
    color: #495057;
    margin: 0.25rem 0;
    font-size: 0.85rem;
  }
  
  .debug-file {
    margin-left: 1rem;
    color: #6c757d;
    font-size: 0.8rem;
  }
  
  .debug-stack {
    margin: 0.5rem 0;
  }
  
  .debug-stack summary {
    cursor: pointer;
    color: #007bff;
    font-size: 0.85rem;
  }
  
  .stack-trace {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 3px;
    padding: 0.5rem;
    margin: 0.5rem 0;
    font-size: 0.75rem;
    overflow-x: auto;
    white-space: pre-wrap;
    color: #495057;
  }
  
  .clear-logs-button {
    background: #6c757d;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;
    margin-top: 0.5rem;
  }
  
  .clear-logs-button:hover {
    background: #5a6268;
  }
  
  @media (max-width: 768px) {
    .file-drop-zone {
      padding: 1.5rem;
    }
    
    .file-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    
    .file-details {
      width: 100%;
    }
    
    .remove-button,
    .download-button {
      align-self: flex-end;
      margin-left: 0;
    }
    
    .debug-logs {
      padding: 0.75rem;
    }
    
    .debug-log-item {
      padding: 0.75rem;
    }
    
    .stack-trace {
      font-size: 0.7rem;
    }
  }
</style> 